import { emitTo, listen, type UnlistenFn } from '@tauri-apps/api/event'
import { RoomEvent, type Room } from 'livekit-client'
import { watch } from 'vue'
import { liveKitManager } from '~/lib/livekit/manager'
import { createMicLevelMeter, type MicLevelMeter } from '~/lib/livekit/micLevelMeter'
import { useGroupsStore } from '~/stores/groups'
import { useUserStore } from '~/stores/user'
import { usePlatform } from '~/composables/usePlatform'
import {
  DEBUG_EVENTS,
  DEBUG_WINDOW_LABEL,
  type DebugLogCategory,
  type DebugLogEntry,
  type DebugLogLevel,
  type DebugSnapshot,
} from './debugBus'

const MAX_LOG_ENTRIES = 300

/**
 * Lives only in the main window: accumulates a log ring buffer + builds state snapshots from
 * the Pinia stores and `liveKitManager`, and pushes both to the debug window over Tauri's
 * cross-window event bus. A second, independent window has its own JS heap — Pinia state and
 * the `liveKitManager` singleton are NOT shared with it — so this is the only bridge.
 * `emitTo` a window that doesn't exist yet is a safe no-op, so every push here is best-effort.
 */
class DebugPublisher {
  private started = false
  private nextLogId = 1
  private readonly logBuffer: DebugLogEntry[] = []
  private roomListeners: Array<() => void> = []
  private unlistenFns: UnlistenFn[] = []
  private snapshotFlushTimer: ReturnType<typeof setTimeout> | null = null
  private micLevelMeter: MicLevelMeter | null = null
  private micLevelTimer: ReturnType<typeof setInterval> | null = null
  /**
   * Whether the debug window is open and listening. Every `emitTo` is a Tauri IPC round-trip
   * (`plugin:event|emit_to`) even when the target window doesn't exist, so nothing is pushed until
   * the window asks for a snapshot, and pushing stops again when it's destroyed. The log ring
   * buffer keeps filling regardless and is replayed via `logBatch` on the next request.
   */
  private consumerConnected = false

  start() {
    if (this.started) return
    this.started = true

    const groupsStore = useGroupsStore()
    const userStore = useUserStore()
    const { isMobile, setMobileOverride } = usePlatform()

    const scheduleSnapshot = () => {
      if (!this.consumerConnected || this.snapshotFlushTimer) return
      this.snapshotFlushTimer = setTimeout(() => {
        this.snapshotFlushTimer = null
        void this.publishSnapshot()
      }, 150)
    }

    groupsStore.$subscribe(() => scheduleSnapshot())
    userStore.$subscribe(() => scheduleSnapshot())
    watch(() => liveKitManager.state, () => scheduleSnapshot(), { deep: true })
    watch(isMobile, () => scheduleSnapshot())

    watch(() => liveKitManager.state.room, (room, previousRoom) => {
      this.detachRoomLogging(previousRoom ?? null)
      this.attachRoomLogging(room)
    }, { immediate: true })

    void listen<void>(DEBUG_EVENTS.requestSnapshot, () => {
      this.consumerConnected = true
      this.startMicLevelStream()
      this.log('system', 'info', 'Debug-окно запросило снапшот')
      void emitTo(DEBUG_WINDOW_LABEL, DEBUG_EVENTS.logBatch, this.logBuffer).catch(() => undefined)
      void this.publishSnapshot()
    }).then(unlisten => this.unlistenFns.push(unlisten))

    // Tauri delivers `tauri://destroyed` only to listeners targeting the destroyed window's
    // label, so the main window subscribes with an explicit target.
    void listen<void>('tauri://destroyed', () => {
      this.consumerConnected = false
      this.stopMicLevelStream()
      this.log('system', 'info', 'Debug-окно закрыто')
    }, { target: { kind: 'WebviewWindow', label: DEBUG_WINDOW_LABEL } })
      .then(unlisten => this.unlistenFns.push(unlisten))

    void listen<boolean>(DEBUG_EVENTS.setMobileLayout, (event) => {
      this.log('system', 'info', `Debug-окно переключило мобильную вёрстку: ${event.payload ? 'вкл' : 'выкл'}`)
      setMobileOverride(event.payload)
    }).then(unlisten => this.unlistenFns.push(unlisten))
  }

  private buildSnapshot(): DebugSnapshot {
    const groupsStore = useGroupsStore()
    const userStore = useUserStore()
    const state = liveKitManager.state

    return {
      ts: Date.now(),
      currentUser: userStore.user,
      groups: groupsStore.groups,
      selectedGroupId: groupsStore.selectedGroupId,
      selectedChannelId: groupsStore.selectedChannelId,
      groupDetails: groupsStore.groupDetails,
      groupUsers: groupsStore.groupUsers,
      isMobile: usePlatform().isMobile.value,
      liveKit: {
        roomState: state.roomState,
        isReconnecting: state.isReconnecting,
        error: state.error,
        activeVoiceChannelId: state.activeVoiceChannelId,
        isMicMuted: state.isMicMuted,
        isDeafened: state.isDeafened,
        isCameraEnabled: state.isCameraEnabled,
        isScreenShareEnabled: state.isScreenShareEnabled,
        isNoiseSuppressionEnabled: state.isNoiseSuppressionEnabled,
        isNoiseSuppressionPending: state.isNoiseSuppressionPending,
        isEchoCancellationEnabled: state.isEchoCancellationEnabled,
        micVolume: state.micVolume,
        speakerVolume: state.speakerVolume,
        videoQuality: state.videoQuality,
        screenShareQuality: state.screenShareQuality,
        selectedMicId: state.selectedMicId,
        selectedCameraId: state.selectedCameraId,
        selectedSpeakerId: state.selectedSpeakerId,
        participants: state.participants,
      },
    }
  }

  private async publishSnapshot() {
    if (!this.consumerConnected) return
    await emitTo(DEBUG_WINDOW_LABEL, DEBUG_EVENTS.snapshot, this.buildSnapshot()).catch(() => undefined)
  }

  // Streamed separately from `publishSnapshot()`'s debounced 150ms flush — this is the same
  // zero-latency Web Audio source `<MicLevelMeter>` uses in the call-controls dropdown, so a
  // debounced/deep-watch-driven push would defeat the entire point of showing it here. Runs only
  // while the debug window is open: at 2 emits/s it was the bulk of the idle IPC traffic.
  private startMicLevelStream() {
    if (this.micLevelTimer) return
    this.micLevelMeter ??= createMicLevelMeter()
    this.micLevelTimer = setInterval(() => {
      void emitTo(DEBUG_WINDOW_LABEL, DEBUG_EVENTS.micLevel, this.micLevelMeter!.level.value).catch(() => undefined)
    }, 500)
  }

  private stopMicLevelStream() {
    if (!this.micLevelTimer) return
    clearInterval(this.micLevelTimer)
    this.micLevelTimer = null
  }

  /** Public so other modules (e.g. `speakingAttributePublisher`) can surface their own
   * activity in the same "События LiveKit" log instead of only `console.warn`ing into a
   * console the user has to remember to have open. */
  log(category: DebugLogCategory, level: DebugLogLevel, message: string) {
    const entry: DebugLogEntry = { id: this.nextLogId++, ts: Date.now(), category, level, message }
    this.logBuffer.push(entry)
    if (this.logBuffer.length > MAX_LOG_ENTRIES) this.logBuffer.shift()
    if (!this.consumerConnected) return
    void emitTo(DEBUG_WINDOW_LABEL, DEBUG_EVENTS.log, entry).catch(() => undefined)
  }

  private attachRoomLogging(room: Room | null) {
    if (!room) return
    // A dedicated set of listeners purely for the debug log — independent of
    // `LiveKitManager.installRoomListeners`'s own listeners (different concern, different
    // lifecycle), so this never has to touch `manager.ts`/`session.ts` internals.
    const on = <E extends Parameters<Room['on']>[0]>(event: E, handler: (...args: any[]) => void) => {
      room.on(event as any, handler as any)
      this.roomListeners.push(() => room.off(event as any, handler as any))
    }

    on(RoomEvent.ConnectionStateChanged, (state) => this.log('livekit', 'info', `Состояние подключения: ${state}`))
    on(RoomEvent.Reconnecting, () => this.log('livekit', 'warn', 'Переподключение...'))
    on(RoomEvent.Reconnected, () => this.log('livekit', 'info', 'Переподключение выполнено'))
    on(RoomEvent.Disconnected, (reason) => this.log('livekit', 'warn', `Отключено от комнаты (reason=${reason ?? 'неизвестно'})`))
    on(RoomEvent.ParticipantConnected, (p) => this.log('livekit', 'info', `Участник подключился: ${p.identity}`))
    on(RoomEvent.ParticipantDisconnected, (p) => this.log('livekit', 'info', `Участник отключился: ${p.identity}`))
    on(RoomEvent.TrackPublished, (pub, p) => this.log('livekit', 'info', `Трек опубликован: ${pub.source} (${p.identity})`))
    on(RoomEvent.TrackUnpublished, (pub, p) => this.log('livekit', 'info', `Трек снят с публикации: ${pub.source} (${p.identity})`))
    on(RoomEvent.TrackSubscribed, (_track, pub, p) => this.log('livekit', 'info', `Подписка на трек: ${pub.source} (${p.identity})`))
    on(RoomEvent.TrackMuted, (pub, p) => this.log('livekit', 'info', `Трек заглушен: ${pub.source} (${p.identity})`))
    on(RoomEvent.TrackUnmuted, (pub, p) => this.log('livekit', 'info', `Трек включён: ${pub.source} (${p.identity})`))
    on(RoomEvent.ConnectionQualityChanged, (quality, p) => this.log('livekit', 'info', `Качество соединения: ${quality} (${p.identity})`))
    on(RoomEvent.MediaDevicesError, (error) => this.log('livekit', 'error', `Ошибка устройства: ${error?.message ?? error}`))
    on(RoomEvent.ParticipantAttributesChanged, (changed, p) => this.log('livekit', 'info', `Атрибуты изменены у ${p.identity}${p.isLocal ? ' (я)' : ''}: ${JSON.stringify(changed)}`))

    this.log('livekit', 'info', 'Комната LiveKit создана — слушатели debug-лога подключены')
  }

  private detachRoomLogging(room: Room | null) {
    for (const off of this.roomListeners.splice(0)) off()
    if (room) this.log('livekit', 'info', 'Комната LiveKit закрыта')
  }
}

export const debugPublisher = new DebugPublisher()
